import { capitalize } from 'lodash-es';

// sliceStart('设计标签','设计') => '标签'
export function sliceStart(str: string, target: string) {
  if (str?.startsWith(target)) {
    return str.slice(target.length);
  }
  return str;
}

// sliceEnd('设计标签','标签') => '设计'
export function sliceEnd(str: string, target: string) {
  if (str?.endsWith(target)) {
    return str.slice(0, str.lastIndexOf(target));
  }
  return str;
}

/**
 * 生成4位随机数
 * @returns string
 */
export const generateRandomTimeBasedNumber = () => {
  const currentTime = new Date().getTime();
  const randomTimeOffset = Math.floor(Math.random() * 10000);
  const timeBasedNumber = (currentTime + randomTimeOffset) % 10000;
  return timeBasedNumber.toString();
};

export function getRandomStr(len = 6) {
  // 定义字符集
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let randomStr = '';

  // 随机选择字符并拼接成字符串
  for (let i = 0; i < len; i++) {
    const randomIndex = Math.floor(Math.random() * charactersLength);
    randomStr += characters[randomIndex];
  }

  return randomStr;
}

// 每隔num位插入分隔符
export function stringFormat(str: string, num = 12, flat = '\n') {
  if (num > 0 && str.length > num) {
    const length = Math.ceil((str?.length || 0) / num);
    const list = [];
    for (let i = 1; i <= length; i++) {
      list.push(str.slice((i - 1) * num, i * num));
    }
    return list.join(flat);
  }
  return str;
}

// 判断是否负数：支持数字和类数字的字符串（例如 '-1', '-100%' ）
export function isNegativeNum(value: number | string) {
  if (typeof value === 'string') {
    return value.startsWith('-');
  }
  return value < 0;
}

/** 生成随机字符串 */
export function randomString(length?: number) {
  const len = length || 32;
  const $chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
  const maxPos = $chars.length;
  let pwd = '';
  for (let i = 0; i < len; i++) {
    pwd += $chars.charAt(Math.floor(Math.random() * maxPos));
  }
  return pwd;
}

// 将base64字符串转成ArrayBuffer
export function base64ToArrayBuffer(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

// 把base64字符串的dCryptoKey转成CryptoKey对象
export async function importDecryptKey(decryptKey: string) {
  return await crypto.subtle.importKey(
    'pkcs8',
    new Uint8Array(
      atob(decryptKey)
        .split('')
        .map((c) => c.charCodeAt(0)),
    ),
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    true,
    ['decrypt'],
  );
}

// 解密
export async function decryptMessage(decrypKey: CryptoKey, contentText: BufferSource) {
  // 使用 RSA-OAEP 算法（私钥时使用的配置要与这里匹配）
  const decryptedBuffer = await crypto.subtle.decrypt(
    {
      name: 'RSA-OAEP',
      // 如果导入私钥时指定了 hash 参数，例如 { name: "RSA-OAEP", hash: "SHA-256" }，这里无需重复指定
    },
    decrypKey, // 私钥，需要是 CryptoKey 对象
    contentText, // 待解密的数据，必须为 ArrayBuffer
  );

  // 解密结果为 ArrayBuffer，通过 TextDecoder 将其转换为 UTF-8 字符串
  const decoder = new TextDecoder();
  const decryptedText = decoder.decode(decryptedBuffer);
  return JSON.parse(decryptedText);
}

export function fieldToLable(input: string) {
  return input
    .split('_')
    .map((s) => capitalize(s))
    .join(' ');
}
// 百分比化
export function percent(value: string | number, len = 2) {
  if (!isNumberLike(value) || !isNumberLike(len)) return value;
  return `${(+(value as number) * 100).toFixed(+len)}%`;
}
// 单位转换 K / M / B
// level用于限制数据格式化为K,M,B的不同情况，比如数据超过1E6时需要格式化为M，此时配置unitConversion(2,2)
export function unitConversion({
  value,
  len = 2,
  level = 1,
  defaultValue,
}: {
  value: string | number;
  len?: number;
  level?: number;
  defaultValue?: string | number;
}) {
  if (!isNumberLike(value) || !isNumberLike(len)) return defaultValue ?? value;
  const val = +value;
  switch (true) {
    // 当level大于1时，不格式化为K
    case val >= 1e3 && val < 1e6 && level <= 1: {
      const num = (val / 1e3).toFixed(len);
      const str = `${num}K`;
      return str;
    }
    // 当level大于2时，不格式化为M
    case val >= 1e6 && val < 1e9 && level <= 2: {
      const num = (val / 1e6).toFixed(len);
      const str = `${num}M`;
      return str;
    }
    // 当level大于3时，不格式化为B
    case val >= 1e9 && level <= 3: {
      const num = (val / 1e9).toFixed(len);
      const str = `${num}B`;
      return str;
    }
    default:
      return value;
  }
}

export function isNumberLike(value: any) {
  return (
    (!Number.isNaN(value) && typeof value === 'number') ||
    /^-?(\d+(\.\d+)?|\.\d+)([eE][-+]?\d+)?$/.test(value)
  );
}

export function s1000(value: number) {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
/**
 * 获取 URL 上的 Query 参数
 * @param url 指定 URL，默认为当前页面 URL
 * @param key 指定要获取的 Query 参数 key，不传则返回全部
 * @returns 返回单个值或全部键值对对象，若参数不存在则返回 undefined
 */
export function getQueryParams({
  url,
  key,
}: {
  url?: string;
  key?: string;
}): string | Record<string, string> | undefined {
  const targetUrl = url || window.location.href;
  const searchIndex = targetUrl.indexOf('?');

  if (searchIndex === -1) {
    return key ? undefined : {};
  }

  const search = targetUrl.slice(searchIndex + 1);
  const hashIndex = search.indexOf('#');
  const queryString = hashIndex !== -1 ? search.slice(0, hashIndex) : search;
  const params = new URLSearchParams(queryString);

  if (key) {
    return params.get(key) || undefined;
  }

  const result: Record<string, string> = {};
  params.forEach((value, paramKey) => {
    result[paramKey] = value;
  });
  return result;
}

